/**
 * Statistische Entitäten Page - Tabbed view for BBSR statistical regions (INKAR)
 *
 * These are non-administrative regions used for statistical analysis:
 * - Metropolregionen (11)
 * - Großstadtregionen (~30)
 * - Raumordnungsregionen (96)
 * - Arbeitsmarktregionen (258)
 * - IHK-Bezirke (79)
 * - Mittelbereiche (~850)
 * - Kreisregionen (~320)
 */

import { useState, useMemo } from 'react'
import { Search, ChevronLeft, ChevronRight, Globe2, Building, Briefcase, MapPin, Layers, Database, Map, Users } from 'lucide-react'
import { useClassificationList } from '../hooks/queries'
import type { ClassificationType } from '../types'
import { cn } from '../../lib/utils'

interface TabConfig {
  id: ClassificationType
  label: string
  shortLabel: string
  icon: React.ReactNode
  description: string
}

const TABS: TabConfig[] = [
  {
    id: 'metropolitan-regions',
    label: 'Metropolregionen',
    shortLabel: 'Metropol',
    icon: <Globe2 className="w-4 h-4" />,
    description: 'Großräumige Wirtschafts- und Funktionsräume um Großstädte'
  },
  {
    id: 'major-city-regions',
    label: 'Großstadtregionen',
    shortLabel: 'Großstadt',
    icon: <Building className="w-4 h-4" />,
    description: 'Funktionale Regionen um Großstädte'
  },
  {
    id: 'spatial-planning-regions',
    label: 'Raumordnungsregionen',
    shortLabel: 'ROR',
    icon: <Map className="w-4 h-4" />,
    description: 'Analyseregionen für übergreifende Raumbeobachtung'
  },
  {
    id: 'labor-market-regions',
    label: 'Arbeitsmarktregionen',
    shortLabel: 'AMR',
    icon: <Users className="w-4 h-4" />,
    description: 'Funktionale Abgrenzung nach Pendlerverflechtungen'
  },
  {
    id: 'ihk-districts',
    label: 'IHK-Bezirke',
    shortLabel: 'IHK',
    icon: <Briefcase className="w-4 h-4" />,
    description: 'Bezirke der Industrie- und Handelskammern'
  },
  {
    id: 'bbsr-middle-areas',
    label: 'Mittelbereiche',
    shortLabel: 'Mittel',
    icon: <MapPin className="w-4 h-4" />,
    description: 'Funktionale Regionen um Mittelzentren'
  },
  {
    id: 'county-regions',
    label: 'Kreisregionen',
    shortLabel: 'Kreis',
    icon: <Layers className="w-4 h-4" />,
    description: 'Zusammenfassungen von Kreisen zu funktionalen Regionen'
  },
]

function RegionTable({ regionType }: { regionType: ClassificationType }) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(0)
  const limit = 50

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, error } = useClassificationList(regionType, {
    limit,
    offset: page * limit,
    search: debouncedSearch,
  })

  const totalPages = data ? Math.ceil(data.total / limit) : 0
  const tabConfig = TABS.find(t => t.id === regionType)

  return (
    <div className="space-y-4">
      {/* Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="w-5 h-5 text-gray-400" />
          <div>
            <span className="text-sm text-gray-600">{tabConfig?.description}</span>
            {data && (
              <span className="ml-2 text-sm font-medium text-gray-900">
                ({data.total.toLocaleString('de-DE')} Einträge)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Code oder Name suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        ) : error ? (
          <div className="p-4 text-red-600">Fehler beim Laden der Daten</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Indikatoren
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data?.results.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap font-mono">
                      {row.code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {row.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      {row.indicator_count ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {row.indicator_count.toLocaleString('de-DE')}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {data?.results.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                      Keine Einträge gefunden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Seite {page + 1} von {totalPages} ({data.total.toLocaleString('de-DE')} Einträge)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={cn(
                'p-2 rounded-lg border transition-colors',
                page === 0
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              )}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className={cn(
                'p-2 rounded-lg border transition-colors',
                page >= totalPages - 1
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              )}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function StatistischeEntitaetenPage() {
  const [activeTab, setActiveTab] = useState<ClassificationType>('metropolitan-regions')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Statistische Entitäten</h1>
        <p className="text-gray-500 mt-1">BBSR-Regionen aus INKAR (nicht-administrative Gebietsgliederungen)</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-1" aria-label="Tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.shortLabel}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <RegionTable regionType={activeTab} />
    </div>
  )
}
